/**
 * Theme Config Service
 *
 * API service for theme and branding configuration.
 */

import type { ThemeAssetsInput, ThemeColorsInput, UpdateThemeConfigInput } from '@tonnie/schemas';

import { TonnieClient } from '../client/base';

// ═══════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════

/** The colour and asset slots, as `PATCH /api/config/theme` validates them. */
export type ThemeColors = ThemeColorsInput;
export type ThemeAssets = ThemeAssetsInput;

export interface ThemeConfig {
  colors?: ThemeColors;
  assets?: ThemeAssets;
  isActive: boolean;
  version: string;
}

export type { UpdateThemeConfigInput };

// ═══════════════════════════════════════════════════════════════════════
// SERVICE
// ═══════════════════════════════════════════════════════════════════════

export class ThemeConfigService {
  constructor(private client: TonnieClient) {}

  /**
   * Get the active theme configuration for the current tenant.
   */
  async getThemeConfig(): Promise<ThemeConfig> {
    return this.client.get<ThemeConfig>('/api/config/theme');
  }

  /**
   * Update (upsert) the theme configuration.
   */
  async updateThemeConfig(input: UpdateThemeConfigInput): Promise<ThemeConfig> {
    return this.client.patch<ThemeConfig>('/api/config/theme', input);
  }
}

/**
 * Factory function to create a ThemeConfigService instance.
 */
export function createThemeConfigService(client: TonnieClient): ThemeConfigService {
  return new ThemeConfigService(client);
}
